import {
  useToast,
} from '@NitrexGroup/primevue/usetoast';
import {
  onMounted,
  onUnmounted,
  reactive,
  shallowRef,
} from 'vue';

import useLibraryI18n from '../i18n/composable.ts';
import dayjs from '../plugins/dayjs.ts';
import {
  EToastLife,
} from '../types/toast.ts';
import {
  isDefined,
} from '../utils/common.ts';
import {
  clonePlainData,
} from '../utils/object.ts';
import {
  matchSequences,
} from '../utils/sequence-diff.ts';
import {
  IThreeWayDiffOptions,
  IThreeWayDiffRow,
  IThreeWayVersions,
  THREE_WAY_CHOICES,
  THREE_WAY_VERSIONS,
  TThreeWayChoice,
  TThreeWayChoices,
  TThreeWayDiffRowKind,
  TThreeWayVersion,
  applyThreeWayChoices,
  computeThreeWayDiff,
  getDefaultThreeWayChoices,
  getValueAtDiffPath,
} from '../utils/three-way-diff.ts';

export interface IMergeConflictField {
  label: string,
  /** Formats the value; for a list segment, one item of the list. */
  format: (fieldValue: unknown) => string,
  /**
   * Short name of a list item, used to say where a list segment is, e.g. "After Step 2".
   * Defaults to `format`.
   */
  formatListItemName?: (listItem: unknown, index: number) => string,
}

export interface IMergeConflictAuthor {
  name?: string,
  savedAt?: Date,
}

export interface IMergeConflictValidationVersions<T> {
  original: T,
  edited: T,
  /** `undefined` when the data no longer exists on the server. */
  latest: T | undefined,
}

export interface IMergeConflictOptions<T> {
  /** The value when editing started. */
  original: T,
  /** The original value with the user's changes. */
  edited: T,
  /** Resolve with `undefined` when the data no longer exists on the server. */
  fetchLatest: () => Promise<T | undefined>,
  /** Persists the value to save. Reject to keep the modal open. */
  submit: (merged: T) => Promise<unknown>,
  /**
   * Returns an error message when the edited changes can no longer be applied to the latest
   * value, e.g. because a different load is running now. Nothing is saved in that case.
   * Without a message for a missing latest value, a generic one is shown.
   */
  validateLatest?: (versions: IMergeConflictValidationVersions<T>) => string | undefined,
  /**
   * Edits that will not be saved, e.g. changes to steps that have passed, shown above the
   * conflicts under a "Not saved" heading. Any warning opens the modal, even without changes.
   */
  getWarnings?: (versions: IThreeWayVersions<T>) => string[],
  /**
   * Adjusts the value right before it is submitted, with or without the modal, e.g. to take
   * fields that only the server changes from the latest version.
   */
  finalize?: (merged: T, versions: IThreeWayVersions<T>) => T,
  /**
   * Label and formatter of the value of a row. Read related values through `row.versionPaths`,
   * e.g. the list item a field belongs to. Defaults to the path and a generic formatter.
   */
  describeField?: (row: IThreeWayDiffRow, versions: IThreeWayVersions<T>) => Partial<IMergeConflictField>,
  /** Who saved the latest value and when; shown with the server version. */
  getLatestAuthor?: (latest: T) => IMergeConflictAuthor | undefined,
  diffOptions?: IThreeWayDiffOptions,
  title?: string,
}

interface IMergeConflictResultWithValue<T> {
  event: 'submitted' | 'cancelled',
  value: T,
}

interface IMergeConflictResultWithoutValue {
  event: 'dismissed' | 'failed',
}

/**
 * - `submitted`: the value was saved; `value` is what was saved.
 * - `cancelled`: the user discarded the changes in the modal; `value` is the latest value.
 * - `dismissed`: the user closed the modal to continue editing. Nothing was saved.
 * - `failed`: the latest value is missing or failed `validateLatest`. Nothing was saved.
 */
export type TMergeConflictResult<T> = IMergeConflictResultWithValue<T> | IMergeConflictResultWithoutValue;

export interface IMergeConflictTextPart {
  text: string,
  /** Differs from the original value. */
  isChanged: boolean,
}

/**
 * - `present`: the version has the value.
 * - `removed`: the version does not have a value that the original has.
 * - `missing`: neither the version nor the original has a value.
 */
export type TMergeConflictValueStatus = 'present' | 'removed' | 'missing';

export interface IMergeConflictValueView {
  status: TMergeConflictValueStatus,
  /** One line per list item, otherwise a single line. */
  lines: IMergeConflictTextPart[][],
}

export interface IMergeConflictRowView {
  id: string,
  label: string,
  /** Where a list segment is in the list, e.g. "After Step 2". */
  position?: string,
  kind: TThreeWayDiffRowKind,
  values: Record<TThreeWayVersion, IMergeConflictValueView>,
  /** The items of both versions, for list segments where both versions inserted items. */
  combinedValue?: IMergeConflictValueView,
}

export interface IMergeConflictDialog {
  title: string | undefined,
  warnings: string[],
  /** Who saved the latest value and when, e.g. "M. Weber · 10:31". */
  latestAuthor: string | undefined,
  /** Values changed in both versions; each needs a choice. */
  conflicts: IMergeConflictRowView[],
  /** Values changed only in the latest version; they are kept unless reverted. */
  serverChanges: IMergeConflictRowView[],
  choices: TThreeWayChoices,
  isSubmitting: boolean,
}

export interface IActiveMergeConflict {
  dialog: IMergeConflictDialog,
  setChoice: (rowId: string, choice: TThreeWayChoice) => void,
  chooseForAllConflicts: (version: TThreeWayVersion) => void,
  /** Rejects when the submit fails; the modal then stays open. */
  save: () => Promise<void>,
  cancel: () => void,
  dismiss: () => void,
}

const I18N_PREFIX = 'components/modal-merge-conflict/modal-merge-conflict';
const TIME_FORMAT = 'LT';
const DATE_TIME_FORMAT = 'L, LT';

const EMPTY_FIELD_VALUE_TEXT = '–';
const LABEL_SEPARATOR = ' › ';
const AUTHOR_SEPARATOR = ' · ';
const TEXT_TOKEN_PATTERN = /\s+|[\p{L}\p{N}]+(?:[.,]\p{N}+)*|\S/gu;
const WHITESPACE_PATTERN = /^\s+$/;

const activeMergeConflict = shallowRef<IActiveMergeConflict>();
let mountedHostCount = 0;

function translate(key: string, ...args: unknown[]): string {
  return useLibraryI18n().t(key, ...args);
}

function formatFieldValue(fieldValue: unknown): string {
  if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
    return EMPTY_FIELD_VALUE_TEXT;
  }
  if (typeof fieldValue === 'boolean') {
    return fieldValue
      ? translate('common.yes')
      : translate('common.no');
  }
  if (typeof fieldValue === 'object') {
    return JSON.stringify(fieldValue);
  }
  return String(fieldValue);
}

/** Lists the path of the version that has the value, with list positions counted from 1. */
function getGenericLabel(row: IThreeWayDiffRow): string {
  const path = row.versionPaths.latest ?? row.versionPaths.edited ?? row.versionPaths.original ?? row.path;
  if (path.length === 0) {
    return translate(`${I18N_PREFIX}.value`);
  }
  return path
    .map((segment) => (typeof segment === 'number' ? String(segment + 1) : segment))
    .join(LABEL_SEPARATOR);
}

function mergeAdjacentParts(parts: IMergeConflictTextPart[]): IMergeConflictTextPart[] {
  return parts.reduce<IMergeConflictTextPart[]>((mergedParts, part) => {
    const previousPart = mergedParts[mergedParts.length - 1];
    if (previousPart?.isChanged === part.isChanged) {
      previousPart.text += part.text;
    } else {
      mergedParts.push({
        ...part,
      });
    }
    return mergedParts;
  }, []);
}

/** Marks the words and characters of `text` that are not in `originalText`. */
function markChangedParts(text: string, originalText: string): IMergeConflictTextPart[] {
  const tokens = text.match(TEXT_TOKEN_PATTERN) ?? [];
  const originalTokens = originalText.match(TEXT_TOKEN_PATTERN) ?? [];
  const matchedIndices = new Set(matchSequences(tokens, originalTokens, (token, originalToken) => token === originalToken)
    .map((match) => match.firstIndex));
  const isTokenChanged = tokens.map((token, index) => !WHITESPACE_PATTERN.test(token) && !matchedIndices.has(index));
  return mergeAdjacentParts(tokens.map((token, index) => ({
    text: token,
    // whitespace between two changed words is marked too, so the marked text stays connected
    isChanged: isTokenChanged[index]
      || (WHITESPACE_PATTERN.test(token) && !!isTokenChanged[index - 1] && !!isTokenChanged[index + 1]),
  })));
}

function createValueViews(row: IThreeWayDiffRow, format: IMergeConflictField['format']): Record<TThreeWayVersion, IMergeConflictValueView> {
  const originalText = row.original.isPresent ? format(row.original.value) : undefined;

  function createValueView(version: TThreeWayVersion): IMergeConflictValueView {
    const entry = row[version];
    if (!entry.isPresent) {
      return {
        status: originalText === undefined ? 'missing' : 'removed',
        lines: [],
      };
    }
    const text = format(entry.value);
    let parts: IMergeConflictTextPart[];
    if (version === 'original') {
      parts = [
        {
          text,
          isChanged: false,
        },
      ];
    } else if (originalText === undefined) {
      parts = [
        {
          text,
          isChanged: true,
        },
      ];
    } else {
      parts = markChangedParts(text, originalText);
    }
    return {
      status: 'present',
      lines: [
        parts,
      ],
    };
  }

  return {
    original: createValueView('original'),
    latest: createValueView('latest'),
    edited: createValueView('edited'),
  };
}

function createSegmentValueView(items: unknown[], originalTexts: string[], format: IMergeConflictField['format'], isOriginal: boolean): IMergeConflictValueView {
  if (items.length === 0) {
    return {
      status: originalTexts.length > 0 && !isOriginal ? 'removed' : 'missing',
      lines: [],
    };
  }
  return {
    status: 'present',
    lines: items.map((item, index) => {
      const text = format(item);
      const originalTextAtIndex = originalTexts[index];
      if (isOriginal || originalTexts.includes(text)) {
        return [
          {
            text,
            isChanged: false,
          },
        ];
      }
      // an item at the same place as an original item is marked like a changed value
      return originalTextAtIndex === undefined
        ? [
          {
            text,
            isChanged: true,
          },
        ]
        : markChangedParts(text, originalTextAtIndex);
    }),
  };
}

function getSegmentItems(row: IThreeWayDiffRow, version: TThreeWayVersion): unknown[] {
  const entry = row[version];
  return entry.isPresent ? entry.value as unknown[] : [];
}

function getSegmentPosition<T>(
  row: IThreeWayDiffRow,
  versions: IThreeWayVersions<T>,
  formatListItemName: NonNullable<IMergeConflictField['formatListItemName']>,
): string | undefined {
  if (!row.segment) {
    return undefined;
  }
  const listPath = row.versionPaths.latest;
  const list = listPath && getValueAtDiffPath(versions.latest, listPath);
  const start = row.segment.ranges.latest.start;
  if (!Array.isArray(list) || start === 0) {
    return translate(`${I18N_PREFIX}.positionStart`);
  }
  return translate(`${I18N_PREFIX}.positionAfter`, {
    item: formatListItemName(list[start - 1], start - 1),
  });
}

function createRowView<T>(row: IThreeWayDiffRow, versions: IThreeWayVersions<T>, options: IMergeConflictOptions<T>): IMergeConflictRowView {
  const describedField = options.describeField?.(row, versions);
  const format = describedField?.format ?? formatFieldValue;
  const label = describedField?.label ?? getGenericLabel(row);
  if (!row.segment) {
    return {
      id: row.id,
      label,
      kind: row.kind,
      values: createValueViews(row, format),
    };
  }

  const originalTexts = getSegmentItems(row, 'original').map(format);
  return {
    id: row.id,
    label,
    position: getSegmentPosition(row, versions, describedField?.formatListItemName ?? format),
    kind: row.kind,
    values: {
      original: createSegmentValueView(getSegmentItems(row, 'original'), originalTexts, format, true),
      latest: createSegmentValueView(getSegmentItems(row, 'latest'), originalTexts, format, false),
      edited: createSegmentValueView(getSegmentItems(row, 'edited'), originalTexts, format, false),
    },
    combinedValue: row.segment.canCombine
      ? createSegmentValueView([
        ...getSegmentItems(row, 'latest'),
        ...getSegmentItems(row, 'edited'),
      ], originalTexts, format, false)
      : undefined,
  };
}

function formatAuthor(author: IMergeConflictAuthor | undefined): string | undefined {
  const authorParts: string[] = [];
  if (author?.name) {
    authorParts.push(author.name);
  }
  if (author?.savedAt) {
    const savedAt = dayjs(author.savedAt);
    authorParts.push(savedAt.format(savedAt.isSame(dayjs(), 'day') ? TIME_FORMAT : DATE_TIME_FORMAT));
  }
  return authorParts.length > 0 ? authorParts.join(AUTHOR_SEPARATOR) : undefined;
}

function finalizeMerged<T>(merged: T, versions: IThreeWayVersions<T>, options: IMergeConflictOptions<T>): T {
  return options.finalize
    ? options.finalize(merged, versions)
    : merged;
}

function openMergeConflictDialog<T>(
  versions: IThreeWayVersions<T>,
  rows: IThreeWayDiffRow[],
  warnings: string[],
  options: IMergeConflictOptions<T>,
): Promise<TMergeConflictResult<T>> {
  return new Promise((resolve) => {
    const rowViews = rows.map((row) => createRowView(row, versions, options));
    const dialog = reactive<IMergeConflictDialog>({
      title: options.title,
      warnings,
      latestAuthor: formatAuthor(options.getLatestAuthor?.(versions.latest)),
      conflicts: rowViews.filter((rowView) => rowView.kind === 'changedInBoth'),
      serverChanges: rowViews.filter((rowView) => rowView.kind === 'changedInLatest'),
      choices: getDefaultThreeWayChoices(rows),
      isSubmitting: false,
    });

    function close(result: TMergeConflictResult<T>) {
      activeMergeConflict.value = undefined;
      resolve(result);
    }

    activeMergeConflict.value = {
      dialog,
      setChoice: (rowId, choice) => {
        dialog.choices[rowId] = choice;
      },
      chooseForAllConflicts: (version) => {
        dialog.conflicts.forEach((conflict) => {
          dialog.choices[conflict.id] = version;
        });
      },
      save: () => {
        const merged = finalizeMerged(applyThreeWayChoices(versions, dialog.choices, options.diffOptions), versions, options);
        dialog.isSubmitting = true;
        return Promise.resolve(options.submit(merged))
          .then(() => close({
            event: 'submitted',
            value: merged,
          }))
          .finally(() => {
            dialog.isSubmitting = false;
          });
      },
      cancel: () => close({
        event: 'cancelled',
        value: versions.latest,
      }),
      dismiss: () => close({
        event: 'dismissed',
      }),
    };
  });
}

/**
 * Saves edited data only after checking whether it was changed elsewhere in the meantime.
 *
 * `saveWithConflictCheck` fetches the latest value and compares it with the value at edit start
 * and the edited value. Without changes elsewhere and without warnings it submits the edited value
 * directly. Otherwise it opens the merge conflict modal: values changed only elsewhere are applied
 * unless the user reverts them, and for values changed on both sides the user chooses which one
 * to keep. Requires `ModalMergeConflictHost`, mounted once at the root of the app.
 *
 * The returned promise rejects when `fetchLatest` fails, or when `submit` fails without the modal.
 */
export function useMergeConflict() {
  const toast = useToast();
  const isSaving = shallowRef(false);

  function resolveAgainstLatest<T>(options: IMergeConflictOptions<T>, latestValue: T | undefined): Promise<TMergeConflictResult<T>> {
    const original = clonePlainData(options.original);
    const edited = clonePlainData(options.edited);
    const latest = latestValue === undefined
      ? undefined
      : clonePlainData(latestValue);

    const validationMessage = options.validateLatest?.({
      original,
      edited,
      latest,
    });
    if (latest === undefined || isDefined(validationMessage)) {
      toast.add({
        severity: 'error',
        summary: translate('common.error'),
        detail: validationMessage ?? translate(`${I18N_PREFIX}.latestMissing`),
        life: EToastLife.ERROR,
      });
      return Promise.resolve<TMergeConflictResult<T>>({
        event: 'failed',
      });
    }

    const versions: IThreeWayVersions<T> = {
      original,
      latest,
      edited,
    };
    const rows = computeThreeWayDiff(versions, options.diffOptions);
    const warnings = options.getWarnings?.(versions) ?? [];
    if (rows.length === 0 && warnings.length === 0) {
      const merged = finalizeMerged(versions.edited, versions, options);
      return Promise.resolve(options.submit(merged)).then((): TMergeConflictResult<T> => ({
        event: 'submitted',
        value: merged,
      }));
    }
    return openMergeConflictDialog(versions, rows, warnings, options);
  }

  function saveWithConflictCheck<T>(options: IMergeConflictOptions<T>): Promise<TMergeConflictResult<T>> {
    if (mountedHostCount === 0) {
      throw new Error('ModalMergeConflictHost is not mounted.');
    }
    if (isSaving.value || isDefined(activeMergeConflict.value)) {
      throw new Error('Another save with conflict check is still in progress.');
    }

    isSaving.value = true;
    return options.fetchLatest()
      .then((latestValue) => resolveAgainstLatest(options, latestValue))
      .finally(() => {
        isSaving.value = false;
      });
  }

  return {
    /** True from the refetch until the save finished or the modal was closed. */
    isSaving,
    saveWithConflictCheck,
  };
}

/** Only for `ModalMergeConflictHost`. */
export function useMergeConflictHost() {
  onMounted(() => {
    mountedHostCount++;
  });
  onUnmounted(() => {
    mountedHostCount--;
    activeMergeConflict.value?.dismiss();
  });

  return {
    activeMergeConflict,
  };
}

/** The versions a conflict can be resolved with, in the order they are shown. */
export function getConflictChoices(rowView: IMergeConflictRowView): TThreeWayChoice[] {
  return rowView.combinedValue
    ? [
      ...THREE_WAY_CHOICES,
    ]
    : [
      ...THREE_WAY_VERSIONS,
    ];
}
